/*
 * Copyright (C) 2024 Curity AB. All rights reserved.
 *
 * The contents of this file are the property of Curity AB.
 * You may not copy or use this file, in either source code
 * or executable form, except in compliance with terms
 * set by Curity AB.
 *
 * For further information, please contact Curity AB.
 */

/**
 * Contains a set JS-usable functions and types for SD-JWT.
 */
@file:OptIn(ExperimentalJsExport::class, DelicateCoroutinesApi::class)

import io.curity.ssi.crypto.HashAlgorithmsNames
import io.curity.ssi.crypto.createHashFunction
import io.curity.ssi.jose.JwsUtil
import io.curity.ssi.js.JsToJsonConverter
import io.curity.ssi.js.JsonToJsConverter
import io.curity.ssi.sdjwt.JsonPath
import io.curity.ssi.sdjwt.SdJwtConcealer
import io.curity.ssi.sdjwt.SdJwtParser
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.promise
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.intOrNull
import kotlin.js.Json
import kotlin.js.Promise

/**
 * Parses a string representation of an SD-JWT into a [SdJwtParseResult] or throws exception
 * if the string does not represent a well-formed SD-JWT.
 */
@JsExport
fun parseSdJwt(sdJwtString: String): Promise<SdJwtParseResult> = GlobalScope.promise {
    val sdJwt = SdJwtParser.decodeFromString(
        sdJwtString,
        jwtDecoder = { JwsUtil.unsafeParseJws(it) }
    )

    SdJwtParseResult(
        jwtString = sdJwt.jwtString,
        kbJwtString = sdJwt.keyBindingJwtString,
        disclosures = sdJwt.disclosureStrings.toTypedArray(),
        disclosedClaims = mapObject(sdJwt.disclosedCustomClaims)
    )
}

/**
 * Represents the result of parsing an SD-JWT
 * @property jwtString the issuer JWT
 * @property kbJwtString the optional Key Binding (KB) JWT
 * @property disclosures the array with all included disclosures
 * @property disclosedClaims the custom claims with optional disclosure information
 */
@JsExport
class SdJwtParseResult(
    val jwtString: String,
    val kbJwtString: String?,
    val disclosures: Array<String>,
    val disclosedClaims: SdObject,
)

/**
 * Define a hierarchy of [SdValue], i.e. Selectively Disclosure values with disclosure information
 */
@JsExport
sealed interface SdValue

@JsExport
class SdObject internal constructor(
    propertyMap: Map<String, SdProperty>,
) : SdValue {
    val properties: Array<SdProperty> = propertyMap.values.toTypedArray()
}

@JsExport
class SdArray(
    val values: Array<SdArrayValue>,
) : SdValue

@JsExport
class SdPrimitive(
    val value: Any?
) : SdValue

@JsExport
class SdArrayValue(
    val value: SdValue,
    val disclosure: String?
)

@JsExport
class SdProperty(
    val name: String,
    val value: SdValue,
    val disclosure: String?
)

private fun map(input: io.curity.ssi.sdjwt.SdValue): SdValue =
    when (input) {
        is io.curity.ssi.sdjwt.SdValue.Array -> mapArray(input)
        is io.curity.ssi.sdjwt.SdValue.Object -> mapObject(input)
        is io.curity.ssi.sdjwt.SdValue.Primitive -> mapPrimitive(input)
    }

private fun mapArray(input: io.curity.ssi.sdjwt.SdValue.Array) =
    SdArray(
        input.elements
            .map { SdArrayValue(map(it.value), it.disclosureString) }
            .toTypedArray()
    )

private fun mapObject(input: io.curity.ssi.sdjwt.SdValue.Object) =
    SdObject(
        input.members.mapValues { SdProperty(it.value.name, map(it.value.value), it.value.disclosureString) }
    )

private fun mapPrimitive(input: io.curity.ssi.sdjwt.SdValue.Primitive): SdValue {
    val value = input.value
        ?: return SdPrimitive(null)
    return SdPrimitive(
        if (value.isString) {
            value.content
        } else {
            value.intOrNull ?: value.booleanOrNull ?: value.doubleOrNull
        }
    )
}

/**
 * Conceals the properties of an object, i.e., make those properties selectively disclosable
 * @param input the object to conceal
 * @param pathsToConceal the paths to the properties or array elements to conceal
 */
@JsExport
fun concealSdJwt(
    input: Json,
    pathsToConceal: Array<Array<Any>>
): Promise<ConcealResult> = GlobalScope.promise {
    val inputJson = JsToJsonConverter.convertJson(input)
    val paths: List<JsonPath> = pathsToConceal.map { JsonPath.build(*it) }
    val concealer = SdJwtConcealer(
        HashAlgorithmsNames.SHA_256,
        createHashFunction(HashAlgorithmsNames.SHA_256)
            ?: throw IllegalStateException("Unable to create SHA-256 function")
    ) { path, _ ->
        paths.any { path.matches(it) }
    }
    val result = concealer.conceal(inputJson)
    ConcealResult(
        output = JsonToJsConverter.convertValue(result.obj).unsafeCast<Json>(),
        disclosures = result.disclosures.map { it.toDisclosureString() }.toTypedArray(),
    )

}

/**
 * The result of the concealment process
 * @property output the concealed object
 * @property disclosures the array of disclosure strings
 */
@JsExport
class ConcealResult(
    val output: Json,
    val disclosures: Array<String>
)
